// src/pages/roles/RoleDetailsPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VpnKey as VpnKeyIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Components
import RoleForm from '../components/RoleForm';
import RolePermissionsManager from '../components/RolePermissionsManager';
import RoleStaffManager from '../components/RoleStaffManager';

// Hooks
import { useRoles } from '../hooks/useRoles';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../auth/hooks/usePermissions';
import PageHeader from '../../../components/common/PageHeader';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function RoleDetailsPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { roleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'edit' ? 'edit' : 'view';
  const { t } = useTranslation(['roles', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const permissions = usePermissions();

  // State
  const [mode, setMode] = useState(initialMode);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    currentRole,
    fetchRoleById,
    updateRole,
    loading,
    error,
    clearError,
  } = useRoles();

  // Load role data
  useEffect(() => {
    if (roleId) {
      fetchRoleById(roleId);
    }
  }, [roleId, fetchRoleById]);

  // Update form data when role loads
  useEffect(() => {
    if (currentRole) {
      setFormData({
        name: currentRole.name || '',
        description: currentRole.description || '',
      });
    }
  }, [currentRole]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('roles:validation.name_required');
    } else if (formData.name.length > 100) {
      errors.name = t('roles:validation.name_max_length');
    }

    if (!formData.description.trim()) {
      errors.description = t('roles:validation.description_required');
    } else if (formData.description.length > 500) {
      errors.description = t('roles:validation.description_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleEdit = useCallback(() => {
    setMode('edit');
    setSearchParams({ mode: 'edit' });
  }, [setSearchParams]);

  const handleCancelEdit = useCallback(() => {
    setMode('view');
    setSearchParams({});
    // Reset form data
    if (currentRole) {
      setFormData({
        name: currentRole.name || '',
        description: currentRole.description || '',
      });
    }
    setValidationErrors({});
  }, [currentRole, setSearchParams]);

  const handleSave = useCallback(async () => {
    if (!validateForm() || !currentRole) {
      return;
    }

    try {
      const result = await updateRole(currentRole.role_id, formData);

      if (result.success) {
        showSuccess(t('roles:success.role_updated'));
        setMode('view');
        setSearchParams({});
        // Refresh the role data
        await fetchRoleById(roleId);
      } else {
        showError(result.error || t('roles:errors.update_failed'));
      }
    } catch (error) {
      showError(t('roles:errors.unexpected_error'));
    }
  }, [validateForm, currentRole, formData, updateRole, showSuccess, showError, setSearchParams, fetchRoleById, roleId, t]);

  const handleBack = useCallback(() => {
    navigateBack('/roles');
  }, [navigateBack]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleRoleUpdate = useCallback(() => {
    // Refresh role data after updates
    if (roleId) {
      fetchRoleById(roleId);
    }
  }, [roleId, fetchRoleById]);

  // Check permissions
  const canUpdate = permissions.hasScope('role:update');

  // Computed values
  const isLoading = loading || !currentRole;

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentRole) {
    return (
      <Box>
        <Alert severity="error">
          {t('roles:messages.role_not_found')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={handleBack}
        title={currentRole.name}
        subtitle={t('roles:subtitle')}
        actions={<>
          <Box display="flex" alignItems="center" gap={2}>
            {mode === 'view' ? (
              canUpdate && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  {t('roles:edit_role')}
                </Button>
              )
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  {t('common:buttons.cancel')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? t('roles:actions.saving') : t('roles:actions.save')}
                </Button>
              </Stack>
            )}
          </Box>
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Role Overview Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('roles:role_information')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {currentRole.description}
                  </Typography>
                </Box>
                
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    icon={<PeopleIcon />}
                    label={t('roles:staff_manager.assigned_count', { 
                      count: currentRole.staff_count || 0,
                      defaultValue_plural: t('roles:staff_manager.assigned_count_plural', { count: currentRole.staff_count || 0 })
                    })}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<VpnKeyIcon />}
                    label={t('roles:permissions_manager.assigned_count', { 
                      count: currentRole.permissions_count || 0,
                      defaultValue_plural: t('roles:permissions_manager.assigned_count_plural', { count: currentRole.permissions_count || 0 })
                    })}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('roles:labels.role_id')}
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {currentRole.role_id}
                </Typography>
                
                <Typography variant="caption" color="text.secondary">
                  {t('roles:labels.department')}
                </Typography>
                <Typography variant="body2">
                  {currentRole.department_name || t('common:messages.no_data')}
                </Typography>
                
                {currentRole.created_at && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {t('roles:labels.created')}
                    </Typography>
                    <Typography variant="body2">
                      <LocalizedDateDistance date={currentRole.created_at} />
                    </Typography>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="role management tabs"
          variant="fullWidth"
        >
          <Tab label={t('roles:tabs.role_details')} id="role-tab-0" aria-controls="role-tabpanel-0" />
          <Tab label={t('roles:tabs.permissions')} id="role-tab-1" aria-controls="role-tabpanel-1" />
          <Tab label={t('roles:tabs.staff_members')} id="role-tab-2" aria-controls="role-tabpanel-2" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {mode === 'edit' ? (
          <RoleForm
            formData={formData}
            onInputChange={handleInputChange}
            validationErrors={validationErrors}
            loading={loading}
            mode="edit"
          />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('roles:role_details')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('roles:labels.name')}
                  </Typography>
                  <Typography variant="body1">
                    {currentRole.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('roles:labels.description')}
                  </Typography>
                  <Typography variant="body1">
                    {currentRole.description}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <RolePermissionsManager
          role={currentRole}
          onUpdate={handleRoleUpdate}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <RoleStaffManager
          role={currentRole}
          onUpdate={handleRoleUpdate}
        />
      </TabPanel>
    </Box>
  );
}